import axios from 'axios';

const ARTIFACT_SERVICE = process.env.REACT_APP_ARTIFACT_SERVICE_URL || '/api/artifact-service';
const SESSION_KEY = 'praxys_session_v1';
const PRAXYS_PRODUCTION_ORIGIN = 'https://praxys.ai';

const sessionUserId = (): string => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return typeof parsed?.user?.email === 'string' ? parsed.user.email : '';
  } catch {
    return '';
  }
};

const client = axios.create({ timeout: 120000 });
client.interceptors.request.use((config) => {
  const userId = sessionUserId();
  if (userId) {
    config.headers = (config.headers || {}) as any;
    (config.headers as any)['X-User-Id'] = userId;
  }
  return config;
});

export interface ArtifactDeliveryStatus {
  artifact_id: string;
  task_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  security: {
    status: 'QUARANTINED' | 'SAFE' | 'REJECTED' | 'ERROR' | string;
    message: string;
    scanner: string;
    sha256: string;
    detected_mime_type: string;
  };
  temporary: {
    available: boolean;
    download_url: string;
    expires_at: string;
    download_count: number;
    max_downloads: number;
  };
  google_drive: {
    connected: boolean;
    connector_instance_id: string;
    uploaded: boolean;
    remote_id: string;
    remote_url: string;
    uploaded_at: string;
    connect_profile_target: string;
  };
}

export const getArtifactDelivery = (artifactId: string, forceRescan = false) =>
  client.get<ArtifactDeliveryStatus>(
    `${ARTIFACT_SERVICE}/artifacts/${encodeURIComponent(artifactId)}/delivery`,
    { params: forceRescan ? { force_rescan: true } : undefined },
  );

export const sendArtifactToDrive = (artifactId: string) =>
  client.post<{ google_drive: ArtifactDeliveryStatus['google_drive']; delivery: ArtifactDeliveryStatus }>(
    `${ARTIFACT_SERVICE}/artifacts/${encodeURIComponent(artifactId)}/drive`,
  );

const contentDispositionFilename = (header?: string): string => {
  if (!header) return '';
  const utf8 = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8?.[1]) return decodeURIComponent(utf8[1]);
  const quoted = header.match(/filename\s*=\s*"([^"]+)"/i);
  if (quoted?.[1]) return quoted[1];
  const plain = header.match(/filename\s*=\s*([^;\s]+)/i);
  return plain?.[1] || '';
};

/**
 * Delivery URLs are application URLs, not storage-server URLs. Older Praxys
 * environments may still contain a placeholder such as https://yourdomain in
 * PRAXYS_DOWNLOAD_BASE_URL. Never let that placeholder escape into a browser
 * download. Resolve delivery paths against the currently loaded Praxys app;
 * the production fallback is https://praxys.ai.
 */
export const normalizeArtifactDownloadUrl = (downloadUrl: string): string => {
  const browserOrigin = typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : PRAXYS_PRODUCTION_ORIGIN;
  const appOrigin = browserOrigin && !/yourdomain/i.test(browserOrigin)
    ? browserOrigin
    : PRAXYS_PRODUCTION_ORIGIN;

  if (!downloadUrl) return '';
  try {
    const parsed = new URL(downloadUrl, appOrigin);
    const isPlaceholder = /yourdomain/i.test(parsed.hostname);
    const isArtifactDelivery = parsed.pathname.includes('/api/artifact-service/deliveries/');
    if (isPlaceholder || isArtifactDelivery) {
      return `${appOrigin}${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    return parsed.toString();
  } catch {
    const path = downloadUrl.startsWith('/') ? downloadUrl : `/${downloadUrl}`;
    return `${appOrigin}${path}`;
  }
};

export const downloadTemporaryArtifact = async (downloadUrl: string, fallbackFilename: string) => {
  const normalizedUrl = normalizeArtifactDownloadUrl(downloadUrl);
  const response = await client.get(normalizedUrl, { responseType: 'blob' });
  const blobUrl = window.URL.createObjectURL(response.data as Blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = contentDispositionFilename(response.headers?.['content-disposition']) || fallbackFilename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
};

const scrollToConnectors = () => {
  const headings = Array.from(document.querySelectorAll('h2,h3,h4')) as HTMLElement[];
  const heading = headings.find((node) => node.textContent?.trim().toLowerCase() === 'connectors');
  const target = heading?.closest('section') || heading;
  if (target instanceof HTMLElement) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    target.setAttribute('data-praxys-artifact-storage-target', 'true');
  }
};

/**
 * Open the existing Profile view and focus its Connectors section.
 *
 * AgentDashboard currently owns view state internally rather than through a
 * router, so this intentionally uses its existing user-menu interaction. The
 * hash/custom event are retained as a forward-compatible signal for a future
 * routed Profile implementation.
 */
export const requestProfileConnectorsNavigation = () => {
  window.location.hash = 'profile-connectors';
  window.dispatchEvent(new CustomEvent('praxys:navigate-profile-connectors'));

  const userBadge = document.querySelector('.praxys-user-badge') as HTMLElement | null;
  if (userBadge) userBadge.click();

  window.setTimeout(() => {
    const candidates = Array.from(
      document.querySelectorAll('.praxys-user-dropdown-menu li, .praxys-user-dropdown-menu [role="menuitem"]'),
    ) as HTMLElement[];
    const profileItem = candidates.find((item) => item.textContent?.trim().toLowerCase() === 'profile');
    if (profileItem) {
      profileItem.click();
      window.setTimeout(scrollToConnectors, 150);
      window.setTimeout(scrollToConnectors, 500);
    } else {
      scrollToConnectors();
    }
  }, 100);
};
